export function collapseWithMap(raw) {
  const source = String(raw || "");
  let text = "";
  const toRaw = [];
  let lastSpace = false;
  for (let i = 0; i < source.length; i += 1) {
    if (/\s/.test(source[i])) {
      if (!text || lastSpace) continue;
      text += " ";
      toRaw.push(i);
      lastSpace = true;
      continue;
    }
    lastSpace = false;
    text += source[i];
    toRaw.push(i);
  }
  if (text.endsWith(" ")) {
    text = text.slice(0, -1);
    toRaw.pop();
  }
  return { text, toRaw };
}

function isWordChar(char) {
  return /[\p{L}\p{N}]/u.test(char || "");
}

const STANDALONE_LETTERS = new Set(["a", "à", "â", "y", "ô"]);

export function expandWordBounds(text, start, end) {
  const source = String(text || "");
  let from = Math.max(0, start);
  let to = Math.min(source.length, end);
  while (from < to && !isWordChar(source[from])) from += 1;
  while (to > from && !isWordChar(source[to - 1])) to -= 1;
  while (from > 0 && isWordChar(source[from - 1])) from -= 1;
  while (to < source.length && isWordChar(source[to])) to += 1;
  return { start: from, end: to };
}

export function isPartialWordQuote(after) {
  const trail = String(after || "");
  if (!isWordChar(trail[0])) return false;
  if (trail.length > 1 && isWordChar(trail[1])) return false;
  return !STANDALONE_LETTERS.has(trail[0].toLocaleLowerCase("fr-FR"));
}

function normChunk(chunk) {
  const value = String(chunk || "");
  if (value.length === 1 && /[\u2019\u2018\u02BC\u2032\u00B4`]/.test(value)) return "'";
  return value.normalize("NFC").replace(/[\u2019\u2018\u02BC\u2032\u00B4`]/g, "'");
}

/** Texte comparable, avec le renvoi vers les index d'origine. */
function prepareMatch(source) {
  const chars = [];
  const startAt = [];
  const endAt = [];
  const src = String(source || "");
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (/[\u00AD\u200B\uFEFF]/.test(ch)) {
      i += 1;
      continue;
    }
    if (/\s/.test(ch)) {
      const start = i;
      while (i < src.length && /\s/.test(src[i])) i += 1;
      if (!chars.length || chars[chars.length - 1] === " ") continue;
      chars.push(" ");
      startAt.push(start);
      endAt.push(i);
      continue;
    }
    let j = i + 1;
    while (j < src.length && /\p{M}/u.test(src[j])) j += 1;
    const norm = normChunk(src.slice(i, j));
    for (let k = 0; k < norm.length; k += 1) {
      chars.push(norm[k]);
      startAt.push(i);
      endAt.push(j);
    }
    i = j;
  }
  if (chars[chars.length - 1] === " ") {
    chars.pop();
    startAt.pop();
    endAt.pop();
  }
  return { text: chars.join(""), startAt, endAt };
}

function findLoose(haystack, needle) {
  const hits = [];
  if (!needle) return hits;
  for (let start = 0; start < haystack.length; start += 1) {
    if (haystack[start] !== needle[0]) continue;
    let hi = start;
    let ni = 0;
    while (ni < needle.length && hi < haystack.length) {
      if (haystack[hi] === needle[ni]) {
        hi += 1;
        ni += 1;
        continue;
      }
      if (haystack[hi] === " " && needle[ni] !== " ") {
        hi += 1;
        continue;
      }
      break;
    }
    if (ni === needle.length) hits.push({ start, end: hi });
  }
  return hits;
}

function isBoundedAt(haystack, start, end, needle) {
  if (/\s/.test(needle)) return true;
  if (isWordChar(haystack[start - 1])) return false;
  if (isWordChar(haystack[end])) return false;
  return true;
}

function sourceSpan(hit, prepared) {
  const start = prepared.startAt[hit.start];
  const end = prepared.endAt[hit.end - 1];
  if (start == null || end == null || end <= start) return null;
  return { start, end };
}

function alignForward(local, stored) {
  let li = 0;
  let si = 0;
  while (si < stored.length && li < local.length) {
    if (local[li] === stored[si]) {
      li += 1;
      si += 1;
      continue;
    }
    if (local[li] === " " && stored[si] !== " ") {
      li += 1;
      continue;
    }
    if (stored[si] === " " && local[li] !== " ") {
      si += 1;
      continue;
    }
    break;
  }
  while (li < local.length && local[li] === " ") li += 1;
  while (si < stored.length && stored[si] === " ") si += 1;
  return { li, si };
}

function sharedEdge(local, stored, fromStart) {
  const here = String(local || "").trim();
  const expected = String(stored || "").trim();
  if (!expected) return "skip";
  if (!here) return "edge";
  const page = fromStart ? here : [...here].reverse().join("");
  const saved = fromStart ? expected : [...expected].reverse().join("");
  const { li, si } = alignForward(page, saved);
  const pageDone = li >= page.length;
  const savedDone = si >= saved.length;
  if (!pageDone && !savedDone) return "no";
  if (savedDone) return "yes";
  return page.length >= 12 ? "yes" : "edge";
}

function contextStrength(haystack, index, needle, lead, trail) {
  const leadFit = sharedEdge(haystack.slice(0, index), lead, false);
  const trailFit = sharedEdge(haystack.slice(index + needle.length), trail, true);
  if (leadFit === "no" || trailFit === "no") return -1;
  if (leadFit === "skip" && trailFit === "skip") return 1;
  if (leadFit !== "yes" && trailFit !== "yes") return -1;
  const before = haystack.slice(0, index).trim();
  const after = haystack.slice(index + needle.length).trim();
  return 1 + Math.min(before.length, lead.trim().length) + Math.min(after.length, trail.trim().length);
}

export function bestQuoteSpan(pageText, quote, before = "", after = "") {
  const prepared = prepareMatch(pageText);
  const haystack = prepared.text;
  const needle = prepareMatch(quote).text;
  const lead = prepareMatch(before).text;
  const trail = prepareMatch(after).text;
  if (!needle || !haystack) return null;

  const exact = exactHits(haystack, needle);
  const hits = (exact.length ? exact : findLoose(haystack, needle)).filter((hit) =>
    isBoundedAt(haystack, hit.start, hit.end, needle)
  );
  let best = null;
  let bestScore = 0;
  hits.forEach((hit) => {
    const score = contextStrength(
      haystack,
      hit.start,
      haystack.slice(hit.start, hit.end),
      lead,
      trail
    );
    if (score > bestScore) {
      bestScore = score;
      best = hit;
    }
  });
  if (best) return sourceSpan(best, prepared);

  const minPartial = Math.min(12, needle.length);
  if (needle.length < 12) return null;
  for (let len = Math.min(needle.length - 1, haystack.length); len >= minPartial; len -= 1) {
    if (!lead && !trail && len < 24) continue;
    const prefix = needle.slice(0, len);
    if (!haystack.endsWith(prefix)) continue;
    const start = haystack.length - prefix.length;
    if (contextStrength(haystack, start, prefix, lead, trail) < 0) continue;
    return sourceSpan({ start, end: haystack.length }, prepared);
  }
  for (let len = Math.min(needle.length - 1, haystack.length); len >= minPartial; len -= 1) {
    if (!lead && !trail && len < 24) continue;
    const suffix = needle.slice(needle.length - len);
    if (!haystack.startsWith(suffix)) continue;
    if (contextStrength(haystack, 0, suffix, lead, trail) < 0) continue;
    return sourceSpan({ start: 0, end: suffix.length }, prepared);
  }
  return null;
}

/** Toutes les citations d'un même texte, avec une seule préparation. */
export function findQuoteSpans(pageText, entries) {
  const prepared = prepareMatch(pageText);
  const haystack = prepared.text;
  return (Array.isArray(entries) ? entries : []).map((entry) => {
    const needle = prepareMatch(entry?.quote).text;
    const lead = prepareMatch(entry?.before || "").text;
    const trail = prepareMatch(entry?.after || "").text;
    if (!needle || !haystack) return null;
    const exact = exactHits(haystack, needle);
    const hits = exact.length
      ? exact
      : findLoose(haystack, needle).filter((hit) => isBoundedAt(haystack, hit.start, hit.end, needle));
    let best = null;
    let bestScore = 0;
    hits.forEach((hit) => {
      const matched = haystack.slice(hit.start, hit.end);
      const score = scoreNear(haystack, hit.start, matched, lead, trail);
      if (score > bestScore) {
        bestScore = score;
        best = hit;
      }
    });
    return best ? sourceSpan(best, prepared) : null;
  });
}

/** Même texte que prepareMatch, sans les tableaux d'index : le tri n'en a pas besoin. */
function matchPlain(source) {
  const chars = [];
  const src = String(source || "");
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === "\u00AD" || ch === "\u200B" || ch === "\uFEFF") {
      i += 1;
      continue;
    }
    if (/\s/.test(ch)) {
      while (i < src.length && /\s/.test(src[i])) i += 1;
      if (!chars.length || chars[chars.length - 1] === " ") continue;
      chars.push(" ");
      continue;
    }
    let j = i + 1;
    while (j < src.length && /\p{M}/u.test(src[j])) j += 1;
    const norm = normChunk(src.slice(i, j));
    for (let k = 0; k < norm.length; k += 1) chars.push(norm[k]);
    i = j;
  }
  if (chars[chars.length - 1] === " ") chars.pop();
  return chars.join("");
}

function exactHits(haystack, needle) {
  const hits = [];
  let from = 0;
  while (from <= haystack.length - needle.length) {
    const start = haystack.indexOf(needle, from);
    if (start < 0) break;
    const end = start + needle.length;
    if (isBoundedAt(haystack, start, end, needle)) hits.push({ start, end });
    from = start + 1;
  }
  return hits;
}

/** Le contexte enregistré tient en quelques dizaines de caractères.
 *  On ne relit donc pas tout le livre avant chaque occurrence.
 */
function scoreNear(haystack, index, matched, lead, trail) {
  const slack = 64;
  const from = Math.max(0, index - lead.length - slack);
  const to = Math.min(haystack.length, index + matched.length + trail.length + slack);
  return contextStrength(haystack.slice(from, to), index - from, matched, lead, trail);
}

function bestLocatedHit(haystack, needle, lead, trail) {
  let hits = exactHits(haystack, needle);
  if (!hits.length) {
    hits = findLoose(haystack, needle).filter((hit) =>
      isBoundedAt(haystack, hit.start, hit.end, needle)
    );
  }
  let best = null;
  let bestScore = 0;
  hits.forEach((hit) => {
    const matched = haystack.slice(hit.start, hit.end);
    const score = scoreNear(haystack, hit.start, matched, lead, trail);
    if (score > bestScore) {
      bestScore = score;
      best = hit;
    }
  });
  return best;
}

function isSentenceEnd(char) {
  return char === "." || char === "!" || char === "?" || char === "…";
}

/** Index de lecture de chaque citation dans le livre entier.
 *  Uniquement pour trier une liste : aucune marque n'est écrite.
 *  Une citation introuvable donne null et reste en fin de liste.
 */
export function locateQuoteStarts(pageText, entries) {
  const haystack = matchPlain(pageText);
  return (Array.isArray(entries) ? entries : []).map((entry) => {
    const needle = matchPlain(entry?.quote);
    const lead = matchPlain(entry?.before || "");
    const trail = matchPlain(entry?.after || "");
    if (!needle || !haystack) return null;
    const best = bestLocatedHit(haystack, needle, lead, trail);
    return best ? best.start : null;
  });
}

/** Phrase qui contient la citation, pour préparer un extrait de capture. */
export function excerptAroundQuote(pageText, entry, maxLength = 1000) {
  const haystack = matchPlain(pageText);
  const needle = matchPlain(entry?.quote);
  const lead = matchPlain(entry?.before || "");
  const trail = matchPlain(entry?.after || "");
  if (!needle || !haystack) return "";
  const hit = bestLocatedHit(haystack, needle, lead, trail);
  if (!hit) return "";

  let from = hit.start;
  while (from > 0 && !isSentenceEnd(haystack[from - 1])) from -= 1;
  while (from < hit.start && haystack[from] === " ") from += 1;

  let to = hit.end;
  while (to < haystack.length) {
    const char = haystack[to];
    to += 1;
    if (char === "." && haystack[to] === ".") continue;
    if (isSentenceEnd(char)) break;
  }

  let sentence = haystack.slice(from, to).trim();
  const limit = Math.max(needle.length, Number(maxLength) || 1000);
  if (sentence.length <= limit) return sentence;
  const local = haystack.slice(hit.start, hit.end);
  const at = sentence.indexOf(local);
  const focus = at >= 0 ? at : 0;
  const pad = Math.max(0, limit - local.length);
  const left = Math.min(focus, Math.floor(pad / 2));
  const start = Math.max(0, focus - left);
  return sentence.slice(start, start + limit).trim();
}

export function normalizeLibraryMatchText(value) {
  return String(value || "")
    .replace(/[\u00AD\u200B\uFEFF]/g, "")
    .replace(/[\u2019\u2018\u02BC\u2032\u00B4`]/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .normalize("NFC");
}

/** Première double-page dont le texte contient l'ancre.
 *  Tant que la pagination n'est pas finie, seul le passage entier compte :
 *  un simple début de phrase, répété plus tôt, ne doit pas arrêter la recherche.
 */
export function findSpreadInTexts(texts, anchor, complete = false) {
  const text = normalizeLibraryMatchText(anchor).slice(0, 120);
  if (text.length < 16 || !texts?.length) return null;
  const haystacks = texts.map((item) => normalizeLibraryMatchText(item));
  const exactAt = (needle) => haystacks.findIndex((item) => item.includes(needle));
  const atFull = exactAt(text);
  if (atFull >= 0) return Math.floor(atFull / 2);
  if (!complete) return null;

  const compactHay = haystacks.map((item) => item.replace(/ /g, ""));
  const compactAt = (needle) => {
    const compact = needle.replace(/ /g, "");
    if (compact.length < 16) return -1;
    return compactHay.findIndex((item) => item.includes(compact));
  };
  const compactFull = compactAt(text);
  if (compactFull >= 0) return Math.floor(compactFull / 2);

  for (let len = text.length - 1; len >= 24; len -= 1) {
    const needle = text.slice(0, len);
    const index = exactAt(needle);
    if (index >= 0) return Math.floor(index / 2);
    const compactIndex = compactAt(needle);
    if (compactIndex >= 0) return Math.floor(compactIndex / 2);
  }

  const needle = text.slice(0, Math.min(40, text.length));
  if (needle.length < 16) return null;
  const joinedAt = haystacks.join("\n").indexOf(needle);
  if (joinedAt < 0) return null;
  let cursor = 0;
  for (let index = 0; index < haystacks.length; index += 1) {
    const next = cursor + haystacks[index].length + 1;
    if (joinedAt < next) return Math.floor(index / 2);
    cursor = next;
  }
  return null;
}

export function rawSpan(toRaw, rawLength, start, end) {
  if (!toRaw.length || start < 0 || end <= start || start >= toRaw.length) return null;
  const last = Math.min(end, toRaw.length) - 1;
  return { start: toRaw[start], end: Math.min(rawLength, toRaw[last] + 1) };
}
